/**
 * Cart deck controller — V2 等高列 rows: expand/collapse, live summaries,
 * running total, checkout proxy.
 *
 * Survival architecture (why nothing here touches inner DOM state): cart
 * changes re-render the drawer's inner HTML via the Section Rendering API, so
 * a listener on an inner node or state held on one dies on the first quantity
 * change. Therefore:
 *
 * - clicks are delegated on document
 * - the open row is data-deck-row on the <cart-drawer> ROOT, the one element
 *   a re-render never replaces; CSS keys visibility off it, so after a
 *   refresh the same row is simply still open with zero re-application
 * - summaries and the total are re-derived from /cart.js + VibeDelivery
 *   .getState() on the cart event contract, debounced, and painted into
 *   whatever [data-deck-*] nodes currently exist
 */
(function () {
  'use strict';
  if (window.__cartDeckInit) return;
  window.__cartDeckInit = true;

  function drawer() { return document.getElementById('cart-drawer'); }

  function syncAria() {
    var cd = drawer();
    if (!cd) return;
    var open = cd.getAttribute('data-deck-row');
    cd.querySelectorAll('[data-deck-row-btn]').forEach(function (btn) {
      btn.setAttribute('aria-expanded', String(btn.getAttribute('data-deck-row-btn') === open));
    });
  }

  // Size the open row's panel to the space that actually exists. A vh-based
  // cap can't know the deck's real chrome (row headers, the pinned 配送
  // header, the bar), so on short viewports it overflowed and pushed the
  // total bar past the drawer's clip edge. Measure instead: whatever the
  // deck has left after every fixed strip is what the panel may use — the
  // bar is the one element that must never lose.
  function sizeOpenPanel() {
    var cd = drawer();
    if (!cd) return;
    var open = cd.getAttribute('data-deck-row');
    cd.querySelectorAll('.cart-deck__row-panel').forEach(function (p) { p.style.maxHeight = ''; });
    if (!open) return;
    // Measure AFTER the reflow the attribute change causes — the deck grows
    // at the items zone's expense when a row opens, and measuring the old
    // geometry undercounts the free space.
    requestAnimationFrame(function () {
      var panel = cd.querySelector('.cart-deck__row--' + open + ' .cart-deck__row-panel');
      var deck = cd.querySelector('.cart-deck');
      var bar = cd.querySelector('.cart-deck__bar');
      var deliveryHeader = cd.querySelector('.cart-deck__delivery-header');
      if (!panel || !deck) return;
      var fixed = (bar ? bar.offsetHeight : 0) + (deliveryHeader ? deliveryHeader.offsetHeight : 0);
      cd.querySelectorAll('.cart-deck__row-header').forEach(function (h) { fixed += h.offsetHeight; });
      var free = deck.clientHeight - fixed - 48; /* rows padding + gaps + hairlines */
      // 80px floor: below that the panel is useless — at that point trading a
      // little 配送 body away is the lesser evil, and the bar still holds
      // because 配送 shrinks to its 64px header before anything clips.
      panel.style.maxHeight = Math.max(80, free) + 'px';
    });
  }

  window.addEventListener('resize', function () { setTimeout(sizeOpenPanel, 50); });

  document.addEventListener('click', function (evt) {
    var rowBtn = evt.target.closest('[data-deck-row-btn]');
    if (rowBtn) {
      var cd = drawer();
      if (!cd) return;
      var name = rowBtn.getAttribute('data-deck-row-btn');
      if (cd.getAttribute('data-deck-row') === name) {
        cd.removeAttribute('data-deck-row'); // tap again to tuck it back in
      } else {
        cd.setAttribute('data-deck-row', name);
      }
      syncAria();
      sizeOpenPanel();
      // The upsell carousel measures width at init; a module that initialised
      // inside display:none measured 0. It listens for resize — nudge it now
      // that the panel is actually visible.
      window.dispatchEvent(new Event('resize'));
      return;
    }

    // No theme checkout proxy anymore: the Vibe widget IS the drawer's
    // footer, ending in its own real 前往結帳 button.
  });

  // The Vibe widget sometimes scrolls its container mid-way during init
  // (focus/measurement side effects) — every drawer open should start at
  // the top of the cart, not somewhere down the flow.
  document.addEventListener('dispatch:cart-drawer:open', function () {
    setTimeout(function () {
      var cd = drawer();
      var scroller = cd && cd.querySelector(':scope > .drawer__content');
      if (scroller) scroller.scrollTop = 0;
    }, 150);
  });

  /* ---- live summaries + total ------------------------------------- */

  function money(cents) {
    return '$' + Math.round(cents / 100).toLocaleString();
  }

  function setSum(name, text, isSet) {
    var el = document.querySelector('[data-deck-sum="' + name + '"]');
    if (!el) return;
    el.textContent = text;
    el.classList.toggle('is-set', !!isSet);
  }

  function shippingSummary() {
    var state = null;
    try {
      state = window.VibeDelivery && window.VibeDelivery.getState && window.VibeDelivery.getState();
    } catch (e) { /* widget not ready */ }
    if (!state || !state.selectedMethod) return null;
    var label = state.selectedMethod === 'cvs' ? '超商取貨' : '宅配到家';
    var bits = [label];
    if (typeof state.lastShippingCost === 'number') {
      bits.push(state.lastShippingCost === 0 ? '免運' : '$' + Math.round(state.lastShippingCost));
    }
    return bits;
  }

  var refreshTimer = null;
  function refreshDeck() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(function () {
      syncAria();
      sizeOpenPanel();

      fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root + 'cart.js' : '/cart.js')
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          // 配送 — method + fee from the Vibe widget, chosen CVS store from
          // the cart attributes its apply path writes.
          var bits = shippingSummary();
          if (bits) {
            var attrs = cart.attributes || {};
            for (var k in attrs) {
              if (/門市|store.*name|cvs.*store/i.test(k) && attrs[k]) {
                bits.push(String(attrs[k]).slice(0, 12));
                break;
              }
            }
            setSum('checkout', bits.join(' · '), true);
          } else {
            setSum('checkout', '請選擇配送方式', false);
          }

          // 優惠 — applied discount beats the invitation copy.
          if (cart.total_discount > 0) {
            setSum('deals', '已折 ' + money(cart.total_discount), true);
          } else {
            setSum('deals', '未使用', false);
          }

          // 加購 — count what the upsell block actually rendered. One selector
          // only: product cards and their add buttons are 1:1, so querying
          // both double-counts every product.
          var mount = document.getElementById('cart-drawer-app-upsell');
          var count = mount ? mount.querySelectorAll('.cart-upsell__product').length : 0;
          setSum('addons', count > 0 ? count + ' 件商品可加購' : '加價購優惠', count > 0);

          // Collapse app blocks that are visually empty (gift with zero tiers,
          // discount display with nothing active) so they don't hold a blank
          // band inside their section. Un-hide before measuring — hidden
          // elements measure 0 forever otherwise — and both statements run in
          // the same synchronous reflow, so nothing flashes.
          ['cart-drawer-app-gift', 'cart-drawer-app-discount'].forEach(function (id) {
            var m = document.getElementById(id);
            var widget = m && m.firstElementChild;
            if (!widget) return;
            widget.classList.remove('cart-widget-hidden');
            // 24px: the empty discount display still renders a 12px bar of
            // margins; any real row (a discount line, a gift tier) is ≥30px.
            widget.classList.toggle('cart-widget-hidden', widget.offsetHeight < 24);
          });
        })
        .catch(function () { /* keep whatever is painted */ });
    }, 300);
  }

  [
    'cart:updated',
    'cart:refresh',
    'cart:item-added',
    'cart:rerender-items',
    'on:cart:add',
    'on:line-item:change',
    'dispatch:cart-drawer:refresh',
    'dispatch:cart-drawer:open',
    'shipping:method:changed',
    'ecpayStoreSelected'
  ].forEach(function (name) {
    document.addEventListener(name, refreshDeck);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { setTimeout(refreshDeck, 800); });
  } else {
    setTimeout(refreshDeck, 800);
  }
})();
